import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { AppLayout } from 'components/AppLayout'
import { Button } from 'components/Button'
import {
  formatResultCount,
  ManagementFilterBar,
  ManagementPagination,
  ManagementSearchInput,
  ManagementSelectFilter,
} from 'components/ManagementFilters'
import { Snackbar } from 'components/Snackbar'
import { pageCardClasses } from 'components/AppLayout/styles'
import { MANAGEMENT_PAGE_SIZE } from 'constants/index'
import { routes } from 'constants/routes'
import { commonStrings, organizationManagementStrings } from 'constants/strings'
import { useAdminOrganizations, useClientPagination, useCurrentUser } from 'hooks'
import { EOrganizationMemberRole, EOrganizationPlan } from 'types'
import { matchesSearch } from 'utils'
import { OrganizationsTable } from './OrganizationsTable'
import { OrganizationsCardList } from './OrganizationsCardList'

type OrganizationManagementLocationState = {
  organizationCreated?: boolean
  organizationUpdated?: boolean
}

const planFilterOptions = [
  {
    value: EOrganizationPlan.LIGHT,
    label: organizationManagementStrings.planLabels.LIGHT,
  },
  {
    value: EOrganizationPlan.BASIC,
    label: organizationManagementStrings.planLabels.BASIC,
  },
  {
    value: EOrganizationPlan.INTERNAL,
    label: organizationManagementStrings.planLabels.INTERNAL,
  },
]

export default function OrganizationManagement() {
  const navigate = useNavigate()
  const location = useLocation()
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [planFilter, setPlanFilter] = useState<EOrganizationPlan | ''>('')
  const { organizations, loading, error } = useAdminOrganizations()
  const { data: currentUser } = useCurrentUser()
  const canManageOrganizations = currentUser?.role === EOrganizationMemberRole.ADMIN
  const filteredOrganizations = useMemo(
    () =>
      organizations.filter((organization) => {
        if (!matchesSearch([organization.name], searchKeyword)) {
          return false
        }

        return !(planFilter && organization.plan !== planFilter)
      }),
    [organizations, searchKeyword, planFilter],
  )
  const isFiltering = Boolean(searchKeyword || planFilter)
  const countLabel = formatResultCount(
    filteredOrganizations.length,
    organizations.length,
  )
  const paginationResetKey = `${searchKeyword}\u0000${planFilter}`
  const {
    currentPage,
    totalPage,
    pageItems: paginatedOrganizations,
    goPrev,
    goNext,
  } = useClientPagination(
    filteredOrganizations,
    MANAGEMENT_PAGE_SIZE,
    paginationResetKey,
  )
  const emptyMessage = isFiltering
    ? commonStrings.filteredEmpty
    : organizationManagementStrings.states.empty
  const handleEdit = (organizationId: number) => {
    navigate(
      routes.ORGANIZATION_MANAGEMENT_EDIT.replace(
        ':organizationId',
        String(organizationId),
      ),
    )
  }

  useEffect(() => {
    const state = location.state as OrganizationManagementLocationState | null
    if (state?.organizationCreated) {
      setSnackbarMessage(organizationManagementStrings.messages.createSuccess)
      setIsSnackbarOpen(true)
    } else if (state?.organizationUpdated) {
      setSnackbarMessage(organizationManagementStrings.messages.updateSuccess)
      setIsSnackbarOpen(true)
    } else {
      return
    }

    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
    })
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ])

  return (
    <AppLayout>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <h1 className="text-2xl font-display text-ink">
              {organizationManagementStrings.title}
            </h1>
          </div>
          <div className="flex w-full flex-wrap items-center justify-between gap-3 sm:w-auto sm:justify-end">
            {canManageOrganizations && (
              <Button
                type="button"
                size="sm"
                onClick={() => navigate(routes.ORGANIZATION_MANAGEMENT_CREATE)}
              >
                {organizationManagementStrings.actions.createOrganization}
              </Button>
            )}
            <span className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs font-semibold text-muted">
              {countLabel}
            </span>
            <ManagementPagination
              currentPage={currentPage}
              totalPage={totalPage}
              loading={loading}
              onPrevious={goPrev}
              onNext={goNext}
            />
          </div>
        </header>

        {error && (
          <div className="mt-6">
            <Alert>{error}</Alert>
          </div>
        )}

        <ManagementFilterBar>
          <ManagementSearchInput
            label={commonStrings.filters.searchLabel}
            placeholder={organizationManagementStrings.filters.searchPlaceholder}
            value={searchKeyword}
            onChange={setSearchKeyword}
            onClear={() => setSearchKeyword('')}
          />
          <ManagementSelectFilter
            label={organizationManagementStrings.filters.plan}
            value={planFilter}
            options={planFilterOptions}
            onChange={setPlanFilter}
            onClear={() => setPlanFilter('')}
          />
        </ManagementFilterBar>

        <div className="mt-6 hidden overflow-x-auto rounded-2xl border border-border/70 bg-white table-scroll lg:block">
          <OrganizationsTable
            organizations={paginatedOrganizations}
            loading={loading}
            emptyMessage={emptyMessage}
            onEdit={canManageOrganizations ? handleEdit : undefined}
          />
        </div>
        <div className="mt-6 lg:hidden">
          <OrganizationsCardList
            organizations={paginatedOrganizations}
            loading={loading}
            emptyMessage={emptyMessage}
            onEdit={canManageOrganizations ? handleEdit : undefined}
            className="md:grid-cols-2"
          />
        </div>
      </section>
      <Snackbar
        open={isSnackbarOpen}
        message={snackbarMessage}
        variant="success"
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setIsSnackbarOpen(false)}
      />
    </AppLayout>
  )
}
